import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIStatus } from '../../../enum';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

type ResetPasswordSliceType = {
  tokenFromPath: string | null;
  resetPasswordApiStatus: APIStatus;
  changePasswordApiStatus: APIStatus;
};

const initialState: ResetPasswordSliceType = {
  tokenFromPath: null,
  resetPasswordApiStatus: APIStatus.IDLE,
  changePasswordApiStatus: APIStatus.IDLE,
};

export const checkToken = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'resetPassword/checkToken',
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().resetPasswordSlice.tokenFromPath;
      if (!token) {
        return false;
      }

      const response = (await axios.post(
        'v0_token_verify',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )) as boolean | undefined;
      return response ?? false;
    } catch (e) {
      logError(e, ['resetPasswordSlice', 'checkToken']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendPassword = createAsyncThunk<string | undefined, string, AsyncThunkConfig>(
  'resetPassword/sendPassword',
  async (password, thunkAPI) => {
    try {
      const token = thunkAPI.getState().resetPasswordSlice.tokenFromPath;
      if (!token) {
        return;
      }

      const response = (await axios.post(
        'v0_reset_password',
        { password },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )) as string | undefined; // manager_id
      return response;
    } catch (e) {
      logError(e, ['resetPasswordSlice', 'sendPassword']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const changePassword = createAsyncThunk<string | undefined, string, AsyncThunkConfig>(
  'resetPassword/changePassword',
  async (password, thunkAPI) => {
    try {
      const response = (await axios.post('v0_reset_password', { password })) as string | undefined; // manager_id;
      return response;
    } catch (e) {
      logError(e, ['resetPasswordSlice', 'changePassword']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const resetPasswordSlice = createSlice({
  name: 'resetPassword',
  initialState,
  reducers: {
    updateTokenFromPath: (state, action: PayloadAction<string | null>) => {
      state.tokenFromPath = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendPassword.pending, (state, _action) => {
        state.resetPasswordApiStatus = APIStatus.PENDING;
      })
      .addCase(sendPassword.fulfilled, (state, _action) => {
        state.resetPasswordApiStatus = APIStatus.FULFILLED;
      })
      .addCase(sendPassword.rejected, (state, _action) => {
        state.resetPasswordApiStatus = APIStatus.ERROR;
      })
      .addCase(changePassword.pending, (state, _action) => {
        state.changePasswordApiStatus = APIStatus.PENDING;
      })
      .addCase(changePassword.fulfilled, (state, _action) => {
        state.changePasswordApiStatus = APIStatus.FULFILLED;
      })
      .addCase(changePassword.rejected, (state, _action) => {
        state.changePasswordApiStatus = APIStatus.ERROR;
      });
  },
});

export const { updateTokenFromPath } = resetPasswordSlice.actions;
